/**
 * Secure Logging Service
 * Prevents sensitive data exposure in production
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogContext {
  [key: string]: any;
}

class Logger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.MODE === 'development';
  }

  /**
   * Sanitize sensitive data before logging
   */
  private sanitize(data: any): any {
    if (!data) return data;

    const sensitiveKeys = ['password', 'token', 'accessToken', 'refreshToken', 'idToken', 
                          'authorization', 'secret', 'apiKey', 'apikey', 'api_key'];
    
    if (typeof data === 'object') {
      const sanitized = { ...data };
      Object.keys(sanitized).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof sanitized[key] === 'object') {
          sanitized[key] = this.sanitize(sanitized[key]);
        }
      });
      return sanitized;
    }
    
    return data;
  }

  private log(level: LogLevel, message: string, context?: LogContext): void {
    // Only log in development mode
    if (!this.isDevelopment) {
      // In production, you could send to monitoring service (e.g., Sentry, LogRocket)
      // this.sendToMonitoringService(level, message, context);
      return;
    }

    const sanitizedContext = context ? this.sanitize(context) : undefined;
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${level.toUpperCase()}] ${message}`;

    switch (level) {
      case 'error':
        console.error(logMessage, sanitizedContext || '');
        break;
      case 'warn':
        console.warn(logMessage, sanitizedContext || '');
        break;
      case 'debug':
        console.debug(logMessage, sanitizedContext || '');
        break;
      default:
        console.log(logMessage, sanitizedContext || '');
    }
  }

  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error | any, context?: LogContext): void {
    const errorContext = {
      ...context,
      errorMessage: error?.message,
      errorName: error?.name,
      // Only include stack in development
      ...(this.isDevelopment && error?.stack ? { stack: error.stack } : {})
    };
    this.log('error', message, errorContext);
  }

  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }
}

export const logger = new Logger();
export default logger;
